import React, { useEffect, useImperativeHandle, useState } from 'react'
import { Flex, Text } from '@fundamentals'
import { useRouter } from 'next/router'
import { ArrayDataType, NavigationTabsPropsType, TabBuilder } from './types'
import { Badge, Tab, Tabs } from '@common/material'

export type NavigationAwareTabsRefType = {
  onChangeTab: (name: string, index?: number) => void
  getActiveTab: () => { name: string; index: number }
}

type tabBuilderType = {
  TabBuilder: TabBuilder
}

export const NavigationAwareTabs = React.forwardRef<
  NavigationAwareTabsRefType,
  NavigationTabsPropsType
>(
  (
    {
      tabs,
      initialTab = 0,
      onChangeTab = () => {},
      tabParam = 'tab',
      fullHeightContent,
      children,
      queryOnChangeTab,
      tabHeaderSx,
      withoutQueryParam,
      hideNavigationOnSingleTab,
      ...props
    },
    ref,
  ) => {
    const [activeTab, setActiveTab] = useState(initialTab)
    const [tabLength, setTabLength] = useState(tabs.length)
    const router = useRouter()
    const tabString = !withoutQueryParam ? router.query[tabParam] : initialTab

    const changeTab: (tab: { name?: string }, index) => void = async (
      { name },
      index,
    ) => {
      await setActiveTab(index)
      await onChangeTab(index, name)

      if (!withoutQueryParam) {
        if (queryOnChangeTab) {
          router.query = queryOnChangeTab(router.query)
        }

        if (name) {
          router.query[tabParam] = name
          router.replace(router, undefined, { shallow: true })
        }
      }
    }

    const goToFirstTab = (pushUrl = false) => {
      setActiveTab(0)
      onChangeTab(0, tabs[0]?.name)
      if (withoutQueryParam) {
        return
      }
      if (pushUrl && tabs[0]?.name) {
        router.query[tabParam] = tabs[0]?.name
        router.replace(router, undefined, { shallow: true })
      }
    }

    const getTabIndex = (tabName: string) => {
      const tabIndex = tabName
        ? tabs.findIndex((tab) => tab.name === tabName)
        : activeTab
      return tabIndex === -1 ? 0 : tabIndex
    }

    useImperativeHandle(ref, () => ({
      onChangeTab: (name: string, index?: number) => {
        const tabIndex = !index ? getTabIndex(name) : index
        changeTab({ name }, tabIndex)
      },
      getActiveTab: () => ({ name: tabs[activeTab]?.name, index: activeTab }),
    }))

    useEffect(() => {
      if (tabString) {
        const index = tabs.findIndex((tab) => tab?.name === tabString)
        if (index !== -1 && index !== activeTab) {
          setActiveTab(index)
          onChangeTab(index, tabString as string)
        }
      } else {
        goToFirstTab()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.query])

    useEffect(() => {
      const resetTabs = () => {
        setTabLength(tabs.length)
        goToFirstTab(true)
      }

      if (tabLength !== tabs.length && !initialTab) {
        resetTabs()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabs])

    useEffect(() => {
      if (!tabString) {
        goToFirstTab()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
      <Flex flexDirection='column' alignItems='flex-start' flex={1} {...props}>
        {hideNavigationOnSingleTab && tabs.length === 1 ? (
          <Text variant='subtitle1' mt={3}>
            {tabs[activeTab]?.label}
          </Text>
        ) : (
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => changeTab(tabs[newValue], newValue)}
            variant='scrollable'
            scrollButtons={false}
            sx={{
              width: '100%',
              borderBottom: 1,
              borderColor: 'divider',
              height: '10px',
              ...tabHeaderSx,
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                label={
                  <Flex alignItems='center' gap={1}>
                    {tab?.icon}
                    {tab.label}
                    {tab?.badgeCount != null && tab.badgeCount > 0 && (
                      <Badge count={tab.badgeCount} />
                    )}
                  </Flex>
                }
                key={index}
                data-test={`tab${tab.dataTest || index}`}
                sx={{
                  textTransform: 'none',
                  '&:focus': {
                    outline: 'none',
                  },
                }}
              />
            ))}
          </Tabs>
        )}
        <Flex
          flexDirection='column'
          role='tabpanel'
          width='100%'
          pt={1}
          flex={fullHeightContent ? 1 : null}
        >
          {tabs[activeTab]?.component}
        </Flex>
      </Flex>
    )
  },
) as React.ForwardRefExoticComponent<
  NavigationTabsPropsType & React.RefAttributes<NavigationAwareTabsRefType>
> &
  tabBuilderType

NavigationAwareTabs.TabBuilder = function () {
  const tabs = [] as NavigationTabsPropsType['tabs']
  const builder = {
    addTab: (tab: ArrayDataType<NavigationTabsPropsType['tabs']>) => {
      tabs.push(tab)
      return builder
    },
    build: () => tabs,
  }
  return builder
}
